import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sliders } from "lucide-react";
import { NetworkProbeDisplay } from "./NetworkProbeDisplay";
import { AvailableNetworksList } from "./AvailableNetworksList";
import { NetworkLegend } from "./NetworkLegend";
import { useDeviceEditPermissions } from "@/hooks/use-device-edit-permissions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CellularData {
  networkProbe?: string | {
    probe?: Array<{
      n?: string;
      p?: string;
      a?: number;
      ok?: number;
      s?: number;
      d?: number;
      rsrp?: number;
      rsrq?: number;
      snr?: number;
      rssi?: number;
      csq?: number;
    }>;
  } | {
    n?: string;
    p?: string;
    a?: number;
    ok?: number;
    s?: number;
    d?: number;
    rsrp?: number;
    rsrq?: number;
    snr?: number;
    rssi?: number;
    csq?: number;
  };
  activeProbe?: string | Array<{
    n?: string;
    p?: string;
    a?: number;
    ok?: number;
    s?: number;
    d?: number;
    rsrp?: number;
    rsrq?: number;
    snr?: number;
    rssi?: number;
    csq?: number;
  }>;
  activeProbeLastUpdate?: string;
}

interface CameraConfig {
  cellSteerMode?: string | number;
  cellSteerPlmn?: string;
  cellScanNow?: number;
}

interface CellularNetworkSectionProps {
  cellular?: CellularData;
  cameraConfig?: CameraConfig;
  onUpdateField: (field: string, value: any) => Promise<void>;
  isUpdating: boolean;
  deviceId: string;
}

export function CellularNetworkSection({ 
  cellular, 
  cameraConfig, 
  onUpdateField, 
  isUpdating,
  deviceId
}: CellularNetworkSectionProps) {
  const { canEditDeviceConfig } = useDeviceEditPermissions();
  const [showSteerDialog, setShowSteerDialog] = useState(false);
  const [pendingSteer, setPendingSteer] = useState<{
    plmn: string;
    operatorName: string;
    rat: number;
  } | null>(null);
  const [showAutoDialog, setShowAutoDialog] = useState(false);
  // cellSteerMode = 1 means Manual mode, cellSteerMode = 0 means Auto mode
  // Handle both string and number values for cellSteerMode
  const isManualSteer = cameraConfig?.cellSteerMode === 1 || cameraConfig?.cellSteerMode === "1";
  const steerModeLabel = isManualSteer ? 'MANUAL' : 'AUTO';
  const steerPlmn = cameraConfig?.cellSteerPlmn;

  // Use cameraConfig.cellScanNow directly for button state
  const effectiveScanNow = Number(cameraConfig?.cellScanNow || 0);

  // Debug logging
  console.log('CellularNetworkSection Debug:', {
    cellSteerMode: cameraConfig?.cellSteerMode,
    cellSteerModeType: typeof cameraConfig?.cellSteerMode,
    isManualSteer,
    steerModeLabel,
    isUpdating
  });

  // Parse JSON strings if they exist
  let networkProbe = null;
  let activeProbe: any[] = [];
  
  try {
    if (cellular?.networkProbe) {
      if (typeof cellular.networkProbe === 'string') {
        const parsed = JSON.parse(cellular.networkProbe);
        networkProbe = parsed.probe?.[0] || null;
      } else {
        // When networkProbe is already an object, check if it has a probe array
        const probeObj = cellular.networkProbe as any;
        networkProbe = probeObj.probe?.[0] || cellular.networkProbe;
      }
    }
    
    if (cellular?.activeProbe) {
      if (typeof cellular.activeProbe === 'string') {
        const parsed = JSON.parse(cellular.activeProbe);
        activeProbe = parsed.probe || [];
      } else if (Array.isArray(cellular.activeProbe)) {
        activeProbe = cellular.activeProbe;
      }
    }
  } catch (error) {
    console.error('Error parsing cellular network data:', error);
  }

  const handleSteerToNetwork = async (plmn: string, operatorName: string, rat: number) => {
    setPendingSteer({ plmn, operatorName, rat });
    setShowSteerDialog(true);
  };

  const handleConfirmSteer = async () => {
    if (!pendingSteer) return;

    const { plmn, rat } = pendingSteer;
    
    try {
      // First call: set cellSteerMode = 1
      await onUpdateField('cameraConfig.cellSteerMode', 1);

      // Small delay before second call
      await new Promise(resolve => setTimeout(resolve, 500));

      // Second call: set cellSteerPlmn
      await onUpdateField('cameraConfig.cellSteerPlmn', plmn);

      // Small delay before third call
      await new Promise(resolve => setTimeout(resolve, 500));

      // Third call: set cellSteerAct to the RAT number
      await onUpdateField('cameraConfig.cellSteerAct', rat);
    } catch (error) {
      console.error('Failed to steer to network:', error);
    } finally {
      setShowSteerDialog(false);
      setPendingSteer(null);
    }
  };

  const handleShowAutoDialog = () => {
    setShowAutoDialog(true);
  };

  const handleConfirmAuto = async () => {
    try {
      console.log('Setting cellSteerMode to 0...');
      // First call: set cellSteerMode = 0 (AUTO)
      await onUpdateField('cameraConfig.cellSteerMode', 0);

      // Small delay before second call
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('Setting cellSteerPlmn to AUTO...');
      // Second call: set cellSteerPlmn to "AUTO"
      await onUpdateField('cameraConfig.cellSteerPlmn', 'AUTO');

      // Small delay before third call
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('Setting cellSteerAct to "-1"...');
      // Third call: set cellSteerAct = "-1"
      await onUpdateField('cameraConfig.cellSteerAct', '-1');
      
      console.log('Successfully reverted to AUTO mode with all parameters');
    } catch (error) {
      console.error('Failed to revert to AUTO:', error);
    } finally {
      setShowAutoDialog(false);
    }
  };

  const handleRevertToAuto = async () => {
    console.log('handleRevertToAuto called - Debug:', {
      cellSteerMode: cameraConfig?.cellSteerMode,
      isUpdating,
      steerModeLabel
    });
    
    try {
      console.log('Setting cellSteerMode to 0...');
      // First call: set cellSteerMode = 0 (AUTO)
      await onUpdateField('cameraConfig.cellSteerMode', 0);

      // Small delay before second call
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('Setting cellSteerPlmn to AUTO...');
      // Second call: set cellSteerPlmn to "AUTO"
      await onUpdateField('cameraConfig.cellSteerPlmn', 'AUTO');

      // Small delay before third call
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('Setting cellSteerAct to "-1"...');
      // Third call: set cellSteerAct = "-1"
      await onUpdateField('cameraConfig.cellSteerAct', '-1');
      
      console.log('Successfully reverted to AUTO mode with all parameters');
    } catch (error) {
      console.error('Failed to revert to AUTO:', error);
    }
  };

  const handleTriggerScan = async () => {
    try {
      // First, update the database camera_config
      const { data: currentDevice, error: fetchError } = await supabase
        .from('device_details')
        .select('camera_config')
        .eq('device_id', deviceId)
        .single();

      if (fetchError) {
        console.error('Error fetching current camera_config data:', fetchError);
      }

      // Merge cellScanNow: 1 into existing camera_config object
      const currentCameraConfig = (currentDevice?.camera_config && typeof currentDevice.camera_config === 'object') 
        ? currentDevice.camera_config 
        : {};
      const updatedCameraConfig = { ...currentCameraConfig, cellScanNow: 1 };

      // Update device_details.camera_config in Supabase
      const { error: updateError } = await supabase
        .from('device_details')
        .update({ camera_config: updatedCameraConfig })
        .eq('device_id', deviceId);

      if (updateError) {
        console.error('Error updating camera_config data in DB:', updateError);
      }

      // Then call the device API
      await onUpdateField('cameraConfig.cellScanNow', 1);
    } catch (error) {
      console.error('Error in handleTriggerScan:', error);
      // Still try the API call even if DB update fails
      await onUpdateField('cameraConfig.cellScanNow', 1);
    }
  };

  return (
    <>
      <Card className="col-span-2 border bg-muted/50">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sliders className="h-4 w-4 text-brand-500" />
              <CardTitle className="text-base">Cellular Network</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={steerModeLabel === 'AUTO' ? 'default' : 'secondary'}>
                {steerModeLabel === 'AUTO' ? 'AUTO' : `MANUAL ${steerPlmn}`}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <NetworkProbeDisplay 
            networkProbe={networkProbe}
            title="Current Network"
          />
          
          <AvailableNetworksList
            activeProbe={activeProbe}
            onSteerToNetwork={handleSteerToNetwork}
            onRevertToAuto={handleRevertToAuto}
            onShowAutoDialog={handleShowAutoDialog}
            onTriggerScan={handleTriggerScan}
            isUpdating={isUpdating}
            steerMode={steerModeLabel}
            cellScanNow={effectiveScanNow}
            currentNetworkProbe={networkProbe ? [networkProbe] : []}
            steerPlmn={steerPlmn}
            activeProbeLastUpdate={cellular?.activeProbeLastUpdate}
            canEditDeviceConfig={canEditDeviceConfig}
          />
          
          <NetworkLegend />
        </CardContent>
      </Card>

      <AlertDialog open={showSteerDialog} onOpenChange={setShowSteerDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Switch to {pendingSteer?.operatorName} ({pendingSteer?.plmn})?
            </AlertDialogTitle>
            <AlertDialogDescription>
              We'll ask the device to prefer this network. The request will be sent at its next check-in.
              <br /><br />
              If {pendingSteer?.operatorName} isn't available or the signal is poor, the device may use another network to keep service.
              <br /><br />
              Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSteer}
              style={{ backgroundColor: '#DFEF97', color: 'black' }}
              className="hover:opacity-90"
            >
              Switch network
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showAutoDialog} onOpenChange={setShowAutoDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Switch to Automatic mode?
            </AlertDialogTitle>
            <AlertDialogDescription>
              We'll ask the device to select networks automatically. The request will be sent at its next check-in.
              <br /><br />
              To keep service, the device will choose the best available network and may change networks over time.
              <br /><br />
              Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAuto}
              style={{ backgroundColor: '#DFEF97', color: 'black' }}
              className="hover:opacity-90"
            >
              Switch to Automatic
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}